import { load } from 'cheerio'
import preserveStyling from 'utils/tinyMCE/updateDynamicText/preserveStyling'
import {
  yearTemplate,
  madeInTemplate,
  finalRestingTemplate,
  serviceTemplate,
  memorialContributionsTemplate,
} from 'utils/tinyMCE/templates'
import preserveItalicsAndBolds from 'utils/tinyMCE/updateDynamicText/preserveItalicsAndBolds'
import { handleServiceTemplateUpdate } from 'utils/tinyMCE/updateDynamicText/handleServiceTemplateUpdate'
import { updateHeaders } from '@/utils/tinyMCE/updateDynamicText/updateHeaders'

export const updateTemplates = (dynamicTextProps) => {
  const { editorProps, textTypeProps } = dynamicTextProps
  const { caseDetails } = editorProps
  const case_preferences = caseDetails?.case_preferences || {}
  const { key } = textTypeProps

  const isHeader =
    key.toLowerCase().endsWith('header') &&
    case_preferences &&
    Object.prototype.hasOwnProperty.call(case_preferences, key)

  if (isHeader) {
    updateHeaders(dynamicTextProps)
  } else {
    handleTemplate(dynamicTextProps)
  }
}

const handleTemplate = (dynamicTextProps) => {
  const { textTypeProps, elementProps, editorProps } = dynamicTextProps
  const { element, properties } = elementProps
  const { key } = textTypeProps
  let templateProps = { ...properties }

  const isUnknownTemplate = !templateLegend?.[key]
  if (isUnknownTemplate) {
    console.warn(`Update requested for unknown template key: ${key}`)
    return
  }

  // Handle service and visitation templates
  const currentServices = editorProps.caseDetails?.services || []
  /**
   * data-template attribute value meanings:
   * • "service" - Default service (type="service" with default=true). Auto-updates when default changes.
   * • "visitation" - Default visitation (type="visitation" with default=true). Auto-updates when default changes.
   * • "specific_service" - ANY specific service/visitation looked up by serviceId (type-agnostic).
   *    Both services and visitations use this because the ID lookup works for either type.
   * • "default_service" - Legacy alternative, mapped to "service" for consistency.
   */
  const getDataTemplateAttribute = (templateKey) => {
    if (templateKey === 'default_service' || templateKey === 'service') {
      return 'service'
    }
    if (templateKey === 'visitation') {
      return 'visitation'
    }
    return 'specific_service'
  }
  const dataTemplateAttribute = getDataTemplateAttribute(key)

  const isServiceOrVisitationTemplate =
    key === 'service' ||
    key === 'specific_service' ||
    key === 'default_service' ||
    key === 'visitation'

  // A service block resolves against the case's services, and a service it
  // cannot find removes the block. With no case loaded there is nothing to
  // resolve against, and the removal would be for a service that may well
  // exist: the block is left as stored, and the merge that runs once the
  // case has landed fills it. The customize page reaches this with its
  // pages up before its case, and the first merge's output becomes the
  // box's text, so a block removed here never came back.
  if (isServiceOrVisitationTemplate && !editorProps.caseDetails) return

  if (isServiceOrVisitationTemplate) {
    const serviceResultProps = handleServiceTemplateUpdate({
      key,
      properties,
      currentServices,
      element,
    })
    if (serviceResultProps === null) {
      return
    }
    templateProps = serviceResultProps
  }

  const finalTemplateProps = isServiceOrVisitationTemplate
    ? {
        editorProps,
        templateProps,
        dataTemplateAttribute,
      }
    : {
        // Handle all other templates
        editorProps,
        templateProps,
      }

  if (!templateLegend?.[key]) {
    console.warn(`Template ${key} not found`)
    return
  }
  const templateHtml = templateLegend[key](finalTemplateProps)
  const templateElement = load(templateHtml)

  preserveStyling({ initialElement: element, templateElement })

  preserveItalicsAndBolds({
    initialElement: element,
    targetElement: templateElement,
    targetTag: 'p',
  })

  element.replaceWith(templateElement.html())
}

const templateLegend = {
  date_of_birth_year: yearTemplate,
  date_of_death_year: yearTemplate,
  final_resting: finalRestingTemplate,
  made_in: madeInTemplate,
  service: serviceTemplate,
  specific_service: serviceTemplate, // Also handles specific visitations (lookup by ID is type-agnostic)
  visitation: serviceTemplate,
  memorial_contributions: memorialContributionsTemplate,
}

export default updateTemplates
